import { type SentChatItemInput, createSentChatItem } from '../core/chatItem';
import { EntityStore } from '../core/entityStore';
import { compareSortOrders } from '../core/ids';
import type { ChatAttachment, ChatAuthor, ChatReaction, EmojiId, SentChatItem, WireMediaVariant } from '../core/types';
import type {
  EchoChatPreview,
  EchoMessageType,
  EchoWireAttachment,
  EchoWireLinkPreview,
  EchoWireMention,
  EchoWireMessage,
  EchoWireReaction,
} from './wire';

/** Chat item flag applied when echo author profile data was absent at projection time. */
export const ECHO_PLACEHOLDER_AUTHOR_FLAG = 'echo:placeholder-author';

/** Fallback glyph used when a reaction references emoji not present in the entity store. */
export const ECHO_PLACEHOLDER_EMOJI_GLYPH = '?';

/** Projected reaction chip with the owning echo emoji id preserved for merge/diff callers. */
export interface EchoChatReaction extends ChatReaction {
  readonly emojiId: EmojiId;
}

/** Echo-only fields retained alongside the core chat item after projection. */
export interface EchoChatItemExtras {
  readonly echoType?: EchoMessageType;
  readonly replyToMessageId?: EchoWireMessage['replyToMessageId'];
  readonly mentions: EchoWireMention[];
  readonly linkPreviews: EchoWireLinkPreview[];
  readonly placeholderAuthor?: true;
}

/** Client-renderable sent item shape with echo previews, grouped reactions, and extras preserved. */
export type EchoSentChatItem = Omit<SentChatItem, 'previews' | 'reactions'> &
  EchoChatItemExtras & {
    readonly previews: EchoChatPreview[];
    readonly reactions: EchoChatReaction[];
  };

/** Server-origin chat item shape currently identical to sent items after echo projection. */
export type EchoServerChatItem = EchoSentChatItem;

interface ProjectedAuthor {
  readonly author: ChatAuthor;
  readonly placeholder: boolean;
}

/** Projects one verified echo wire message into a core-compatible ChatItem with echo extras preserved. */
export function projectEchoMessage(wire: EchoWireMessage, store: EntityStore): EchoServerChatItem {
  const projectedAuthor = projectAuthor(wire, store);
  const flags = mergeFlags(wire.flags, projectedAuthor.placeholder);
  const attachments = projectAttachments(wire.attachments);
  const previews = clonePreviews(wire.previews);
  const reactions = projectReactions(wire.reactions, store);
  const input: SentChatItemInput = {
    id: wire.id,
    sortOrder: wire.sortOrder,
    itemKey: wire.id,
    ...(wire.clientRequestId === undefined ? {} : { clientRequestId: wire.clientRequestId }),
    author: projectedAuthor.author,
    createdAt: wire.createdAt,
    ...(wire.rendererRow === undefined ? {} : { rendererRow: wire.rendererRow }),
    body: wire.body,
    ...(wire.bodyHtml === undefined ? {} : { bodyHtml: wire.bodyHtml }),
    ...(flags === undefined ? {} : { flags }),
    containsOnlyAttachments: wire.containsOnlyAttachments,
    containsOnlyEntities: wire.containsOnlyEntities,
    containsOnlyEmojiEntities: wire.containsOnlyEmojiEntities,
    attachments,
    previews,
    reactions,
  };
  const base = createSentChatItem(input);

  return {
    ...base,
    previews,
    reactions,
    mentions: wire.mentions.map((mention) => ({ ...mention })),
    linkPreviews: wire.linkPreviews.map((preview) => ({ ...preview })),
    ...(wire.type === undefined ? {} : { echoType: wire.type }),
    ...(wire.replyToMessageId === undefined ? {} : { replyToMessageId: wire.replyToMessageId }),
    ...(projectedAuthor.placeholder ? { placeholderAuthor: true } : {}),
  };
}

/** Projects messages in input order against the same entity snapshot. */
export function projectEchoMessages(wires: readonly EchoWireMessage[], store: EntityStore): EchoServerChatItem[] {
  return wires.map((wire) => projectEchoMessage(wire, store));
}

function projectAuthor(wire: EchoWireMessage, store: EntityStore): ProjectedAuthor {
  const profile = wire.authorType === 'profile' && wire.rawAuthorId !== null ? store.getProfile(wire.rawAuthorId) : undefined;
  if (profile) {
    return {
      author: {
        id: profile.id,
        ...(wire.authorType === null ? {} : { type: wire.authorType }),
        name: profile.name,
        accent: profile.accent,
        ...(profile.avatarUrl === undefined ? {} : { avatarUrl: profile.avatarUrl }),
      },
      placeholder: false,
    };
  }

  return {
    author: placeholderAuthor(wire.authorId, wire.authorType),
    placeholder: wire.authorType === 'profile' && wire.rawAuthorId !== null,
  };
}

function placeholderAuthor(authorId: ChatAuthor['id'], authorType: string | null): ChatAuthor {
  const hash = hashString(authorId);
  const accent = PLACEHOLDER_ACCENTS[hash % PLACEHOLDER_ACCENTS.length];
  return {
    id: authorId,
    ...(authorType === null ? {} : { type: authorType }),
    name: `Unknown ${authorId.slice(-4).toUpperCase()}`,
    accent,
  };
}

function mergeFlags(flags: readonly string[] | undefined, placeholderAuthorPresent: boolean): string[] | undefined {
  if (!placeholderAuthorPresent) return flags === undefined ? undefined : [...flags];
  const merged = new Set(flags ?? []);
  merged.add(ECHO_PLACEHOLDER_AUTHOR_FLAG);
  return [...merged];
}

function projectReactions(reactions: readonly EchoWireReaction[], store: EntityStore): EchoChatReaction[] {
  const groups = new Map<
    EmojiId,
    {
      count: number;
      minSortOrder: EchoWireReaction['sortOrder'];
      reactedByMe: boolean;
    }
  >();

  for (const reaction of reactions) {
    const current = groups.get(reaction.emojiId);
    if (!current) {
      groups.set(reaction.emojiId, {
        count: 1,
        minSortOrder: reaction.sortOrder,
        reactedByMe: reaction.profileId === store.viewerProfileId,
      });
      continue;
    }

    groups.set(reaction.emojiId, {
      count: current.count + 1,
      minSortOrder: compareSortOrders(reaction.sortOrder, current.minSortOrder) < 0 ? reaction.sortOrder : current.minSortOrder,
      reactedByMe: current.reactedByMe || reaction.profileId === store.viewerProfileId,
    });
  }

  return [...groups.entries()]
    .sort(([leftEmojiId, left], [rightEmojiId, right]) => {
      const order = compareSortOrders(left.minSortOrder, right.minSortOrder);
      if (order !== 0) return order;
      return leftEmojiId < rightEmojiId ? -1 : leftEmojiId > rightEmojiId ? 1 : 0;
    })
    .map(([emojiId, group]) => ({
      emojiId,
      emoji: store.getEmoji(emojiId)?.glyph ?? ECHO_PLACEHOLDER_EMOJI_GLYPH,
      count: group.count,
      ...(group.reactedByMe ? { reactedByMe: true } : {}),
    }));
}

function projectAttachments(attachments: readonly EchoWireAttachment[]): ChatAttachment[] {
  return attachments.map((attachment): ChatAttachment => {
    const kind = kindForContentType(attachment.contentType);
    if (kind === 'file') {
      return {
        id: attachment.id,
        kind,
        url: attachment.originalUrl,
        originalUrl: attachment.originalUrl,
        fileName: attachment.fileName,
        sizeBytes: attachment.sizeBytes,
        ...(attachment.thumbhash === undefined ? {} : { thumbhash: attachment.thumbhash }),
      };
    }

    const display = pickDisplayMedia(attachment);
    const srcSet = srcSetFor(attachment);
    return {
      id: attachment.id,
      kind,
      url: display.url,
      ...(srcSet === undefined ? {} : { srcSet }),
      ...(display.width === undefined ? {} : { width: display.width }),
      ...(display.height === undefined ? {} : { height: display.height }),
      originalUrl: attachment.originalUrl,
      fileName: attachment.fileName,
      sizeBytes: attachment.sizeBytes,
      ...(attachment.thumbhash === undefined ? {} : { thumbhash: attachment.thumbhash }),
    };
  });
}

function pickDisplayMedia(attachment: EchoWireAttachment): {
  readonly url: string;
  readonly width?: number;
  readonly height?: number;
} {
  const variant = attachment.variants?.medium ?? attachment.variants?.large;
  if (variant) {
    return {
      url: variant.url,
      width: variant.width,
      height: variant.height,
    };
  }

  return {
    url: attachment.originalUrl,
    ...(attachment.thumbWidth === undefined ? {} : { width: attachment.thumbWidth }),
    ...(attachment.thumbHeight === undefined ? {} : { height: attachment.thumbHeight }),
  };
}

function srcSetFor(attachment: EchoWireAttachment): string | undefined {
  const { medium, large } = attachment.variants ?? {};
  const entries: WireMediaVariant[] = [];
  if (medium) entries.push(medium);
  if (large) entries.push(large);
  if (entries.length === 0) return undefined;
  return entries.map((entry) => `${entry.url} ${entry.width}w`).join(', ');
}

function kindForContentType(contentType: string): ChatAttachment['kind'] {
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  return 'file';
}

function clonePreviews(previews: readonly EchoChatPreview[]): EchoChatPreview[] {
  return previews.map((preview) => ({
    ...preview,
    ...(preview.image === undefined ? {} : { image: { ...preview.image } }),
  }));
}

function hashString(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

const PLACEHOLDER_ACCENTS = ['#64748b', '#0f766e', '#b45309', '#6d28d9', '#be123c', '#0369a1', '#4d7c0f'];
